/* eslint-disable class-methods-use-this */
/**
 * Own copy of `components/ChevronCell/ChevronwithEsc` — identical behavior,
 * minus the hover tooltip on the name text. That component is shared by
 * ~10 other tables, so it can't be edited in place without touching all of
 * them; this table doesn't need the tooltip (the Name column has enough room
 * that the truncated text plus a dark hover popover reads as visual noise
 * rather than useful, matching PlanRevamp/DimensionsRevamp's tables which
 * don't show one either).
 */
import * as React from 'react';
import {
  CellTemplate,
  Cell,
  Compatible,
  Uncertain,
  UncertainCompatible,
  Id,
  CellStyle,
  keyCodes,
  getCellProperty,
  getCharFromKeyCode,
  isNavigationKey,
  isAlphaNumericKey,
} from '@silevis/reactgrid';
import { Box, Flex, Tooltip } from '@chakra-ui/react';
import { InfoOutlineIcon } from '@chakra-ui/icons';

import ChevronDownIconComponent from 'components/Icons/ChevronDownIcon';
import { FORMULA_ERROR } from 'projectConstants';
import { IndicatorIcon } from 'utils/ReactGridUtils';

export interface ChevronCell extends Cell {
  type: 'chevron';
  text: string;
  isExpanded?: boolean;
  hasChildren?: boolean;
  parentId?: Id;
  indent?: number;
  indicatorType?: string;
  hasFormulaError?: boolean;
  hasFormulaErrorMessage?: any;
  escapePressed?: boolean;
  planPermission?: boolean;
}

export class ChevronCellTemplateOpzV2 implements CellTemplate<ChevronCell> {
  private escapePressed = false;

  planPermission?: boolean;

  constructor(planPermission?: boolean) {
    this.planPermission = planPermission;
  }

  getCompatibleCell(uncertainCell: Uncertain<ChevronCell>): Compatible<ChevronCell> {
    const text = getCellProperty(uncertainCell, 'text', 'string');
    let isExpanded = false;
    try {
      isExpanded = getCellProperty(uncertainCell, 'isExpanded', 'boolean');
    } catch {
      isExpanded = true;
    }
    let indent = -1;
    try {
      indent = getCellProperty(uncertainCell, 'indent', 'number');
    } catch {
      indent = 0;
    }
    let hasChildren = false;
    try {
      hasChildren = getCellProperty(uncertainCell, 'hasChildren', 'boolean');
    } catch {
      hasChildren = false;
    }
    const value = Number.parseFloat(text);
    return { ...uncertainCell, text, value, isExpanded, hasChildren, indent };
  }

  update(
    cell: Compatible<ChevronCell>,
    cellToMerge: UncertainCompatible<ChevronCell>,
  ): Compatible<ChevronCell> {
    return this.getCompatibleCell({
      ...cell,
      isExpanded: cellToMerge.isExpanded,
      text: cellToMerge.text,
    });
  }

  handleKeyDown(
    cell: Compatible<ChevronCell>,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean,
  ): { cell: Compatible<ChevronCell>; enableEditMode: boolean } {
    let enableEditMode = keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER;
    const cellCopy = { ...cell };
    const char = getCharFromKeyCode(keyCode, shift);
    if (keyCode === keyCodes.SPACE && cellCopy.isExpanded !== undefined && !shift) {
      cellCopy.isExpanded = !cellCopy.isExpanded;
    } else if (
      !ctrl &&
      !alt &&
      isAlphaNumericKey(keyCode) &&
      !(shift && keyCode === keyCodes.SPACE)
    ) {
      cellCopy.text = !shift ? char.toLowerCase() : char;
      enableEditMode = true;
    }
    return { cell: cellCopy, enableEditMode };
  }

  getClassName(cell: Compatible<ChevronCell>) {
    const isExpanded = cell.hasChildren ? (cell.isExpanded ? 'expanded' : 'collapsed') : '';
    const className = cell.className ?? '';
    return `${isExpanded} ${className}`;
  }

  getStyle(cell: Compatible<ChevronCell>): CellStyle {
    const indent = cell.indent ?? 0;
    const elementMarginMultiplier = indent * 1.4;
    return { paddingLeft: `calc(${elementMarginMultiplier}em + 2px)` };
  }

  render(
    cell: Compatible<ChevronCell>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<ChevronCell>, commit: boolean) => void,
  ): React.ReactNode {
    const handleCellBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      if (this.escapePressed) {
        this.escapePressed = false; // reset for next time
        return; // Skip blur logic if Escape was just pressed
      }
      onCellChanged(
        this.getCompatibleCell({ ...cell, text: event.currentTarget.value }),
        (event as any).view?.event?.keyCode !== keyCodes.ESCAPE,
      );
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        this.escapePressed = true;
        onCellChanged(
          this.getCompatibleCell({ ...cell, text: cell.text, escapePressed: true }),
          false,
        );
        return;
      }
      if (isAlphaNumericKey(event.keyCode) || isNavigationKey(event.keyCode)) {
        event.stopPropagation();
      }
    };

    // Only reserve room on the right for a chevron/error icon when one will
    // actually render — leaf rows (the vast majority of indicators) have
    // neither, so there's nothing to leave space for. Previously every row
    // reserved the same `calc(100% - 20px)` for the name regardless, which
    // ate into the name column's width even on rows with no chevron to show,
    // leaving long indicator names truncated sooner than necessary.
    const reservedWidth = cell?.hasFormulaError ? '45px' : cell.hasChildren ? '20px' : '0px';

    return !isInEditMode || this.planPermission ? (
      <Flex alignItems={'center'} width={'100%'} justifyContent={'space-between'} minW={0}>
        <Flex alignItems={'center'} gap={1.5} minW={0} width={`calc(100% - ${reservedWidth})`}>
          {!cell.parentId && (
            <Box flexShrink={0} display={'flex'} alignItems={'center'}>
              <IndicatorIcon indicatorType={cell?.indicatorType} />
            </Box>
          )}
          {/* Name takes every pixel the icon/chevron don't need and truncates
              itself — the icon's old 8px side margins plus the text's own
              8px padding burned ~24px that narrow Name columns can't spare. */}
          <p
            style={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              // Child rows have no icon in front of them; keep their original
              // inset so they still read as nested under the parent name.
              paddingLeft: cell.parentId ? '.5rem' : 0,
            }}
          >
            {cell.text}
          </p>
        </Flex>
        {cell.hasChildren ? (
          <>
            {cell?.hasFormulaError && (
              <Box mr={2} maxW={'max-content'} color={'red'} fontSize={'2xs'} position={'relative'}>
                <Tooltip
                  label={cell?.hasFormulaErrorMessage || FORMULA_ERROR}
                  placement={'top'}
                  variant={'primary'}
                >
                  <InfoOutlineIcon height={5} width={5} />
                </Tooltip>
              </Box>
            )}
            <div
              className='chevron'
              onPointerDown={(event_) => {
                event_.stopPropagation();
                onCellChanged(
                  this.getCompatibleCell({ ...cell, isExpanded: !cell.isExpanded }),
                  true,
                );
              }}
            >
              <ChevronDownIconComponent height={22} width={22} direction={true} />
            </div>
          </>
        ) : null}
      </Flex>
    ) : (
      <input
        ref={(input) => {
          if (input) {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
          }
        }}
        defaultValue={cell.text}
        onChange={(event) =>
          onCellChanged(this.getCompatibleCell({ ...cell, text: event.currentTarget.value }), false)
        }
        onBlur={handleCellBlur}
        onCopy={(event) => event.stopPropagation()}
        onCut={(event) => event.stopPropagation()}
        onPaste={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onKeyDown={handleKeyDown}
      />
    );
  }
}
